import React from 'react';
import * as d3Shape from 'd3-shape';
import { Container, StyledSvg, PathElement, PositionedElement } from './styles';

// Constants
const width = 267;
const height = width;
const radius = Math.min(width, height) / 2;
const innerRadius = 105;
const cornerRadius = 8;
const SPACING = 30;

// Types
export type DonutChartData = {
  key: string;
  value: number;
  color: string;
  onPress?: () => void;
  isActive?: boolean;
  disabled?: boolean;
};

type PieChartProps = {
  data?: DonutChartData[];
  hasActiveSelection?: boolean;
  onChartItemPress?: (index: number) => void;
  children?: React.ReactNode;
};

// Helper function to convert hex color with opacity
const hexColorWithOpacity = (hexColor: string, opacity: number): string => {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Parse r, g, b values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
};

export const PieChart: React.FC<PieChartProps> = ({
  data,
  children,
  hasActiveSelection,
  onChartItemPress,
}) => {
  if (!data) return null;

  const pieChartNumbers = data.map((d) => d.value);

  const pieData = d3Shape.pie<number>().padAngle(0.05).sort(null)(
    pieChartNumbers
  );

  const arcGenerator = d3Shape
    .arc<d3Shape.PieArcDatum<number>>()
    .outerRadius(radius)
    .innerRadius(innerRadius)
    .cornerRadius(cornerRadius);

  return (
    <Container width={width} height={height}>
      <StyledSvg width={width} height={height}>
        <g transform={`translate(${width / 2}, ${height / 2})`}>
          {pieData.map((slice, index) => {
            const path = arcGenerator(slice);
            const key = `slice-${index}`;
            const item = data[index];

            const isInactive = hasActiveSelection && !item.isActive;
            const color = isInactive
              ? hexColorWithOpacity(item.color, 25)
              : item.color;

            const onPress = item.onPress
              ? item.onPress
              : () => onChartItemPress?.(index);

            const isClickable =
              !!(item.onPress || onChartItemPress) && !item.disabled;

            return (
              <PathElement
                key={key}
                d={path || ''}
                fill={color}
                isClickable={isClickable}
                onClick={isClickable ? onPress : undefined}
              />
            );
          })}
        </g>
      </StyledSvg>

      {children && (
        <PositionedElement spacing={SPACING}>{children}</PositionedElement>
      )}
    </Container>
  );
};
